import { useState, useEffect, useCallback, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { GeoPoint } from 'firebase/firestore'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography
} from '@mui/material'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import AppBar from '../components/AppBar'
import Wrapper from '../components/Wrapper'
import SuburbAutocomplete from '../components/SuburbAutocomplete'
import routes from './routes'
import { useSessionStore } from '../state'
import {
  checkHandleAvailability,
  claimHandle,
  updateProfile
} from '../services/profileService'
import { geocodeSuburb, getBrowserLocation } from '../services/locationService'
import type { SerializedUser } from '../state'
import type { ResolvedPlace } from '../hooks/usePlacesAutocomplete'

type FormValues = {
  handle: string
  suburb: string
}

type HandleStatus =
  | { type: 'idle' }
  | { type: 'checking' }
  | { type: 'available' }
  | { type: 'taken' }
  | { type: 'invalid'; message: string }
  | { type: 'error' }

export type LocationSource =
  | { type: 'none' }
  | {
      type: 'place'
      coords: { lat: number; lng: number }
      suburb: string
      timestamp: number
    }
  | { type: 'browser'; coords: { lat: number; lng: number }; timestamp: number }

const validateHandleFormat = (value: string): string | null => {
  if (value.length < 3) {
    return 'Handle must be at least 3 characters'
  }
  if (value.length > 30) {
    return 'Handle must be at most 30 characters'
  }
  if (!/^[a-z0-9_-]+$/.test(value)) {
    return 'Only lowercase letters, numbers, hyphens, and underscores allowed'
  }
  if (/^[-_]/.test(value)) {
    return 'Handle cannot start with a hyphen or underscore'
  }
  if (/[-_]$/.test(value)) {
    return 'Handle cannot end with a hyphen or underscore'
  }
  return null
}

const ProfileCompletion = () => {
  const navigate = useNavigate()
  const { user, setProfile } = useSessionStore()
  const [handleStatus, setHandleStatus] = useState<HandleStatus>({
    type: 'idle'
  })
  const [locationSource, setLocationSource] = useState<LocationSource>({
    type: 'none'
  })
  const [isResolvingPlace, setIsResolvingPlace] = useState(false)
  const [locationMessage, setLocationMessage] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const { control, handleSubmit, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      handle: '',
      suburb: ''
    }
  })

  const handleValue = watch('handle')

  const checkHandle = useCallback(async (value: string) => {
    const formatError = validateHandleFormat(value)
    if (formatError) {
      setHandleStatus({ type: 'invalid', message: formatError })
      return
    }

    setHandleStatus({ type: 'checking' })

    try {
      const available = await checkHandleAvailability(value)
      if (available) {
        setHandleStatus({ type: 'available' })
      } else {
        setHandleStatus({ type: 'taken' })
      }
    } catch {
      setHandleStatus({ type: 'error' })
    }
  }, [])

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (!handleValue) {
      setHandleStatus({ type: 'idle' })
      return
    }

    const formatError = validateHandleFormat(handleValue)
    if (formatError) {
      setHandleStatus({ type: 'invalid', message: formatError })
      return
    }

    debounceRef.current = setTimeout(() => {
      checkHandle(handleValue)
    }, 500)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [handleValue, checkHandle])

  const handlePlaceSelect = (place: ResolvedPlace) => {
    setLocationSource({
      type: 'place',
      coords: place.coords,
      suburb: place.formattedDescription,
      timestamp: Date.now()
    })
    setIsResolvingPlace(false)
    setLocationMessage(null)
  }

  const handlePlaceClear = () => {
    setLocationSource({ type: 'none' })
    setLocationMessage(null)
  }

  const handleUseMyLocation = async () => {
    try {
      const coords = await getBrowserLocation()
      setLocationSource({
        type: 'browser',
        coords,
        timestamp: Date.now()
      })
      setLocationMessage('Location obtained successfully')
      setValue('suburb', 'Browser location')
    } catch {
      setLocationMessage(null)
    }
  }

  const getHandleHelperText = (): string => {
    switch (handleStatus.type) {
      case 'checking':
        return 'Checking availability...'
      case 'available':
        return 'Handle is available'
      case 'taken':
        return 'Handle is taken'
      case 'invalid':
        return handleStatus.message
      case 'error':
        return 'Could not verify handle'
      default:
        return ''
    }
  }

  const getHandleHelperColor = (): 'success' | 'error' | 'info' | undefined => {
    switch (handleStatus.type) {
      case 'available':
        return 'success'
      case 'taken':
      case 'invalid':
      case 'error':
        return 'error'
      case 'checking':
        return 'info'
      default:
        return undefined
    }
  }

  const isSubmitDisabled =
    submitting ||
    isResolvingPlace ||
    handleStatus.type === 'checking' ||
    handleStatus.type === 'taken' ||
    handleStatus.type === 'invalid' ||
    handleStatus.type === 'error' ||
    handleStatus.type === 'idle'

  const onSubmit = async (data: FormValues) => {
    if (!user) return

    if (locationSource.type === 'none' && !data.suburb) {
      setSubmitError('A location is required')
      return
    }

    setSubmitting(true)
    setSubmitError(null)

    try {
      let coords: { lat: number; lng: number }
      let suburbString: string

      if (locationSource.type === 'place') {
        coords = locationSource.coords
        suburbString = locationSource.suburb
      } else if (locationSource.type === 'browser') {
        coords = locationSource.coords
        suburbString = 'Browser location'
      } else {
        // Fallback: plain text field mode — use geocodeSuburb
        coords = await geocodeSuburb(data.suburb)
        suburbString = data.suburb
      }

      await claimHandle(user.uid, data.handle)

      await updateProfile(user.uid, {
        handle: data.handle,
        suburb: suburbString,
        location: new GeoPoint(coords.lat, coords.lng),
        guidedDiveFlag: false
      })

      setProfile({
        id: user.uid,
        displayName: user.displayName ?? '',
        email: user.email ?? '',
        handle: data.handle,
        suburb: suburbString,
        location: { latitude: coords.lat, longitude: coords.lng },
        role: 'member',
        guidedDiveFlag: false,
        profileComplete: true,
        creditBalance: 0,
        accreditation: null,
        bankDetails: null,
        allowlist: null,
        perfectRatingCount: null,
        ratingAggregates: null,
        accountStatus: 'active',
        createdAt: new Date(),
        updatedAt: new Date()
      } as SerializedUser)

      navigate(routes.home)
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to save profile. Please try again.'
      setSubmitError(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <AppBar title='Complete Your Profile' />
      <Wrapper>
        <Box
          component='form'
          onSubmit={handleSubmit(onSubmit)}
          display='flex'
          flexDirection='column'
          gap={3}
        >
          <Box>
            <Typography variant='subtitle2' color='text.secondary'>
              Name
            </Typography>
            <Typography variant='body1'>{user?.displayName ?? ''}</Typography>
          </Box>

          <Box>
            <Typography variant='subtitle2' color='text.secondary'>
              Email
            </Typography>
            <Typography variant='body1'>{user?.email ?? ''}</Typography>
          </Box>

          <Controller
            name='handle'
            control={control}
            rules={{ required: 'Handle is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label='Handle'
                placeholder='e.g. ocean-diver42'
                fullWidth
                helperText={getHandleHelperText()}
                error={
                  handleStatus.type === 'taken' ||
                  handleStatus.type === 'invalid' ||
                  handleStatus.type === 'error'
                }
                color={getHandleHelperColor()}
                focused={
                  handleStatus.type === 'available' ||
                  handleStatus.type === 'checking'
                }
                slotProps={{
                  input: {
                    endAdornment:
                      handleStatus.type === 'checking' ? (
                        <CircularProgress size={20} />
                      ) : undefined
                  }
                }}
              />
            )}
          />

          <Box>
            <Controller
              name='suburb'
              control={control}
              rules={{}}
              render={({ field, fieldState }) => (
                <SuburbAutocomplete
                  value={field.value}
                  onChange={value => {
                    field.onChange(value)
                    if (value === '') {
                      handlePlaceClear()
                    }
                  }}
                  onPlaceSelect={place => {
                    field.onChange(place.formattedDescription)
                    handlePlaceSelect(place)
                  }}
                  onPlaceClear={handlePlaceClear}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
            />
            <Box mt={1} display='flex' alignItems='center' gap={1}>
              <Button
                variant='outlined'
                size='small'
                startIcon={<MyLocationIcon />}
                onClick={handleUseMyLocation}
              >
                Use my location
              </Button>
              {locationMessage && (
                <Typography variant='caption' color='success.main'>
                  {locationMessage}
                </Typography>
              )}
            </Box>
          </Box>

          {submitError && <Alert severity='error'>{submitError}</Alert>}

          <Button
            type='submit'
            variant='contained'
            size='large'
            disabled={isSubmitDisabled}
            startIcon={
              submitting ? (
                <CircularProgress size={20} color='inherit' />
              ) : undefined
            }
          >
            {submitting ? 'Saving...' : 'Complete Profile'}
          </Button>
        </Box>
      </Wrapper>
    </>
  )
}

export default ProfileCompletion
